import * as React from 'react';
import IIdentityUploader, { TIdentityUploader } from './IIdentityUploader';
import Dropzone, { ImageFile } from 'react-dropzone';
import Loader from '@appComponents/Loader/Loader';
import axios from 'axios';
import IdentityUploaderStatus from './IdentityUploaderStatuses';
import ProgressBar from '@appComponents/ProgressBar/ProgressBar';
import { supportedIdentityFileTypes } from '@features/staticData/supportedIdentityFileTypes';

import * as style from './IdentityUploaderStyle.module.scss'

export default class IdentityUploader extends React.Component<IIdentityUploader, TIdentityUploader> {

  state: TIdentityUploader = {
    status: IdentityUploaderStatus.empty,
    file: null,
    errors: [],
    previewUrl: '',
    fileId: null,
    progress: 0,
    displayRejectReason: false,
  };

  fileValidation() {
    const { fileSizeMb } = this.props;
    const { file } = this.state;
    let errors: string[] = [];

    if (file) {
      if (file.size > fileSizeMb * 1048576) {
        errors = [...errors, `File size must be less than ${fileSizeMb}MB`];
      } else if (supportedIdentityFileTypes[file.type] === undefined ||
        !supportedIdentityFileTypes[file.type].includes(file.name.slice(file.name.lastIndexOf('.')))) {
        errors = [...errors, 'Unsupported file type'];
      }
    } else {
      errors = [...errors, 'No file selected'];
    }

    if (errors.length > 0) {
      this.setState({
        errors: errors,
        status: IdentityUploaderStatus.error,
      });
    }

    return errors.length === 0;
  }

  // Templates
  // -----------------------

  pendingStateTemplate() {
    return (
      <React.Fragment>
        <h5 className={style.identityUploaderName}>{this.props.filename}</h5>
        <p
          className={[style.identityUploaderStatusIcon,
            style.identityUploaderStatusIconPending].join(' ')
          }
        >
          Pending
        </p>
        <div className={style.identityUploaderBottom}>
          <a
            title="Preview"
            target="_blank"
            className={[style.identityUploaderIcons, style.identityUploaderIconsPreview].join(' ')}
          >
            View
          </a>
          <a
            title="Delete"
            className={[style.identityUploaderIcons, style.identityUploaderIconsDelete].join(' ')}
          >
            Delete
          </a>
        </div>
      </React.Fragment>
    );
  }

  declinedStateTemplate() {
    return (
      <React.Fragment>
        <h5 className={style.identityUploaderName}>{this.props.filename}</h5>
        <p
          className={[style.identityUploaderStatusIcon,
            style.identityUploaderStatusIconRejected].join(' ')
          }
        >
          Rejected
        </p>
        <div className={style.identityUploaderBottom}>
          <a
            title="Preview"
            onClick={() => this.displayRejectReason()}
            className={[style.identityUploaderIcons, style.identityUploaderIconsTemplate].join(' ')}
          >
            View Reason
          </a>
          <a
            title="Delete"
            className={[style.identityUploaderIcons, style.identityUploaderIconsDelete].join(' ')}
          >
            Delete
          </a>
        </div>
      </React.Fragment>
    );
  }

  approvedStateTemplate() {
    return (
      <React.Fragment>
        <h5 className={style.identityUploaderName}>{this.props.filename}</h5>
        <p
          className={[style.identityUploaderStatusIcon,
            style.identityUploaderStatusIconApproved].join(' ')
          }
        >
          Approved
        </p>
      </React.Fragment>
    );
  }

  uploadingStateTemplate() {
    return (
      <React.Fragment>
        <h4 className={[style.identityUploaderHeader, style.identityUploaderHeaderUploading].join(' ')}>Uploading</h4>
        <Loader />
        <div
          className={[
            style.identityUploaderBottom,
            style.identityUploaderBottomGradient,
            style.identityUploaderBottomLeft,
          ].join(' ')}
        >
          <ProgressBar progress={this.state.progress}/>
        </div>
      </React.Fragment>
    );
  }

  deletingStateTemplate() {
    return (
      <React.Fragment>
        <h4 className={[style.identityUploaderHeader, style.identityUploaderHeaderUploading].join(' ')}>Deleting</h4>
          <Loader />
      </React.Fragment>
    );
  }

  emptyStateTemplate() {
    const { description } = this.props;

    return (
      <React.Fragment>
        <Dropzone
          activeClassName={style.doopzoneActive}
          multiple={false}
          onDrop={files => this.onDrop(files)}
          style={{}}
        >
          <h5 className={style.identityUploaderName}>{this.props.filename}</h5>
          <h6 className={style.identityUploaderDescription}>{description}</h6>
          <p
            className={[style.identityUploaderStatusIcon,
              style.identityUploaderStatusIconEmpty].join(' ')
            }
          >
            Upload file
          </p>
        </Dropzone>
      </React.Fragment >
    );
  }

  errorStateTemplate() {
    const { errors } = this.state;
    const errorMessagesList = () => (
      errors.map(error => (
        <p key={error}>{error}</p>
      ))
    );

    return (
      <React.Fragment>
        <h4 className={[style.identityUploaderHeader, style.identityUploaderHeaderError].join(' ')}>Error</h4>
        <div className={style.identityUploaderErrorBody}>{errorMessagesList()}</div>
        <div className={style.identityUploaderBottom}>
          <Dropzone

            multiple={false}
            onDrop={files => this.onDrop(files)}
            style={{}}
          >
            <p
              className={style.identityUploaderChangeFile}
            >
              Click here to upload other file
            </p>
          </Dropzone>
        </div>
      </React.Fragment>
    );
  }

  // Actions
  // ------------------

  handleFileUpload() {
    this.setState({
      status: IdentityUploaderStatus.uploading,
    });

    const formData = new FormData();
    if (this.state.file) {
      formData.append('file', this.state.file);
      formData.append('type', this.props.type);
      if (this.props.privacy) {
        formData.append('privacy', this.props.privacy);
      }

      const config = {
        onUploadProgress: (progressEvent: any) => this.setState({
          progress: Math.floor((progressEvent.loaded * 100) / progressEvent.total),
        }),
      };

      axios.post(`/api/company_documents`, formData, config)
        .then((resp) => {
          this.props.getUploadResponse(resp.data);
        }).catch((err) => {
          const errorKeys = Object.keys(err.response.data.errors);
          const errorsArray = errorKeys.map((key) => {
            return `${key}: ${err.response.data.errors[key]} `;
          });

          this.setState({
            status: IdentityUploaderStatus.error,
            errors: errorsArray,
          });
        });
    }
  }

  displayRejectReason() {
    this.setState({
      displayRejectReason: true,
    });
  }

  onDrop(files:ImageFile[]) {
    this.setState({
      file: files[0],
    }, () => {
      if (this.fileValidation()) {
        this.handleFileUpload();
      }
    });
  }

  resetComponent() {
    this.setState({
      status: IdentityUploaderStatus.empty,
      file: null,
      errors: [],
    });
  }

  renderTemplate() {
    let returnHtml = this.emptyStateTemplate();
    switch (this.state.status){
      case IdentityUploaderStatus.empty:
        returnHtml = this.emptyStateTemplate();
        break;
      case IdentityUploaderStatus.error:
        returnHtml = this.errorStateTemplate();
        break;
      case IdentityUploaderStatus.uploading:
        returnHtml = this.uploadingStateTemplate();
        break;
    }

    return returnHtml;
  }

  render() {
    return (
    <div>
      <div className={style.identityUploader}>
        {this.renderTemplate()}
      </div>
    </div>
    );
  }
}
